import {
  ChangeEvent,
  FC,
  HTMLInputTypeAttribute,
  KeyboardEvent,
  MouseEvent,
  ReactNode,
  Ref,
} from 'react';
import { ErrorMessage } from 'shared/components/Input/ErrorMessage';
import { mergeClasses } from 'shared/utils/mergeClasses';
import { InputErrorType } from 'types/InputErrorType';
import { InputLabel } from './InputLabel';

type InputVariant = 'standard' | 'translucent';
export interface InputProps {
  name: string;
  type?: HTMLInputTypeAttribute;
  ref?: Ref<HTMLInputElement>;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onClick?: (e: MouseEvent<HTMLInputElement>) => void;
  onBlur?: (e: ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (e: ChangeEvent<HTMLInputElement>) => void;
  onKeyUp?: (e: KeyboardEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  value?: string | number;
  className?: string;
  variant?: InputVariant;
  label?: string;
  infoTooltipContent?: string;
  placeholder?: string;
  error?: InputErrorType;
  showErrorWithoutMessage?: boolean;
  endAdornment?: ReactNode;
  ariaLabel?: string;
  disabled?: boolean;
  autocomplete?: string;
  min?: number | string;
  max?: number | string;
  autoFocus?: boolean;
}

export const Input: FC<InputProps> = ({
  name,
  type = 'text',
  ref,
  onChange,
  onClick,
  onBlur,
  onFocus,
  onKeyUp,
  onKeyDown,
  value,
  className = '',
  variant = 'standard',
  placeholder,
  label,
  infoTooltipContent,
  error,
  showErrorWithoutMessage,
  endAdornment,
  ariaLabel,
  disabled,
  autocomplete,
  min,
  max,
  autoFocus,
}) => {
  const isTranslucent = variant === 'translucent';
  const backgroundClasses = isTranslucent
    ? 'bg-white bg-opacity-[0.08] caret-white autofill:transition-colors'
    : 'bg-white disabled:bg-lightBlue disabled:text-gray';

  return (
    <label
      className={mergeClasses(
        error || showErrorWithoutMessage ? '[&>div>input]:placeholder:text-red [&>p]:text-red' : '',
        className,
      )}
      htmlFor={name}
      aria-label={name}
    >
      {label && (
        <InputLabel tooltipId={name} infoTooltipContent={infoTooltipContent}>
          {label}
        </InputLabel>
      )}
      <div className="relative inline-block w-full">
        <input
          ref={ref}
          disabled={disabled}
          aria-label={ariaLabel}
          className={mergeClasses(
            'peer w-full appearance-none rounded-lg border border-transparent px-4 py-[12.5px] text-sm caret-gray outline-none placeholder:text-gray autofill:duration-[5000000ms] disabled:cursor-not-allowed',
            backgroundClasses,
            endAdornment ? 'pr-12' : '',
          )}
          name={name}
          value={value}
          data-test-id={name}
          id={name}
          type={type}
          placeholder={placeholder}
          onChange={onChange}
          onClick={onClick}
          onBlur={onBlur}
          onFocus={onFocus}
          autoComplete={autocomplete}
          onKeyUp={onKeyUp}
          onKeyDown={onKeyDown}
          min={min}
          max={max}
          autoFocus={autoFocus}
        />
        {endAdornment && (
          <span className="absolute bottom-0 right-4 top-0 my-auto flex items-center">
            {endAdornment}
          </span>
        )}
        <div
          className={mergeClasses(
            'pointer-events-none absolute left-0 top-0 h-full w-full rounded-lg border border-gray-300 transition-all duration-75',
            isTranslucent ? 'border-opacity-50 peer-focus-visible:border-gray-200' : '',
            error || showErrorWithoutMessage
              ? 'border-red'
              : 'border-gray-300 peer-focus-visible:border-gray-700',
          )}
        />
      </div>
      {error && <ErrorMessage>{error.message}</ErrorMessage>}
    </label>
  );
};
